import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { format, addMonths } from 'date-fns';
import { reportAPI, paymentAPI, customerAPI, userAPI } from '../services/api';
import { ArrowLeft, ClipboardList, Printer, Loader, Download, ChevronDown, Users, X } from 'lucide-react';
import useDarkReady from '../theme/useDarkReady';
import { indexPrintVars } from '../utils/collectionIndexPrint';
import { useCompanyIdentity } from '../hooks/useCompanyIdentity';

const fmt = (n) => (n === null || n === undefined ? '—' : `₹${new Intl.NumberFormat('en-IN').format(Math.round(n))}`);
const fmtDate = (d) => (d ? format(new Date(d), 'dd/MM/yyyy') : '—');
const toInputDate = (d) => (d ? format(new Date(d), 'yyyy-MM-dd') : '');
const societyAnchorId = (area) => `cs-society-${area.replace(/[^a-zA-Z0-9]+/g, '-')}`;

// Best-effort estimate of which printed page each society's block starts on, derived from
// the fixed print CSS below (A4, 8mm margins, 6.5pt table font) — NOT a guarantee, since
// real pagination depends on the browser/printer and on any row wrapping (long Package/
// Reason text). Page 1 is always the index itself (forced page-break-after on it), so the
// first society starts at page 2.
const PAGE_HEIGHT_MM = 297 - 2 * 8;
const PT_TO_MM = 25.4 / 72;
const PX_TO_MM = 25.4 / 96;
const TABLE_FONT_MM = 6.5 * PT_TO_MM;
const DATA_ROW_HEIGHT_MM = TABLE_FONT_MM * 1.2 + 4 * PX_TO_MM; // line-height ~1.2 + 2px top/bottom padding
const SOCIETY_ROW_HEIGHT_MM = TABLE_FONT_MM * 1.5 + 12 * PX_TO_MM; // line-height 1.5 + 6px top/bottom padding
const USABLE_BODY_HEIGHT_MM = PAGE_HEIGHT_MM - DATA_ROW_HEIGHT_MM; // minus the repeating thead

function estimateStartPages(groups) {
  let page = 2;
  let used = 0;
  return groups.map(g => {
    if (used + SOCIETY_ROW_HEIGHT_MM > USABLE_BODY_HEIGHT_MM) { page += 1; used = 0; }
    const startPage = page;
    used += SOCIETY_ROW_HEIGHT_MM;
    g.rows.forEach(() => {
      if (used + DATA_ROW_HEIGHT_MM > USABLE_BODY_HEIGHT_MM) { page += 1; used = 0; }
      used += DATA_ROW_HEIGHT_MM;
    });
    return startPage;
  });
}

// Bulk-fix which agent gets credit for a society's already-collected payments — e.g. a
// field agent's collections got logged under a fallback "Office" account instead of
// themselves. Preview (dryRun) then Apply, so admin sees exactly what will change first.
// Lives on the Collection Sheet (not the general Payments page) since this is where an
// admin reviews a society's collection round and would spot a misattributed entry.
function ReassignCollectorModal({ initialArea = '', onClose, onApplied }) {
  const [areas, setAreas] = useState([]);
  const [agents, setAgents] = useState([]);
  const [form, setForm] = useState({ area: initialArea, startDate: '', endDate: '', collectedBy: '' });
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);

  useEffect(() => {
    customerAPI.getAreas().then(({ data }) => setAreas(data.data || [])).catch(() => {});
    userAPI.getAll().then(({ data }) => {
      setAgents((data.data || []).filter(u => u.role !== 'customer' && u.isActive));
    }).catch(() => {});
  }, []);

  const canPreview = form.area && form.startDate && form.endDate && form.collectedBy;

  const handlePreview = async (e) => {
    e.preventDefault();
    setLoading(true);
    setPreview(null);
    try {
      const { data } = await paymentAPI.reassignCollector({ ...form, dryRun: true });
      setPreview(data.data);
      if (!data.data.count) toast('No collected payments found for that society/date range', { icon: 'ℹ️' });
    } catch (err) {
      toast.error(err.response?.data?.message || 'Preview failed');
    } finally {
      setLoading(false);
    }
  };

  const handleApply = async () => {
    setApplying(true);
    try {
      const { data } = await paymentAPI.reassignCollector({ ...form, dryRun: false });
      toast.success(`${data.data.updated} payment(s) reassigned to ${data.data.agent}`);
      onApplied();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Reassign failed');
    } finally {
      setApplying(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 560 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Reassign Collection Agent</h3>
          <button className="modal-close" onClick={onClose}><X size={16} /></button>
        </div>
        <form onSubmit={handlePreview}>
          <div className="modal-body">
            <p style={{ fontSize: '.82rem', color: 'var(--text-muted)', marginBottom: 14 }}>
              Pick a society and date range, then the agent who actually collected there — this
              only changes who gets credit, not the amount, customer, or receipt.
            </p>
            <div className="form-group">
              <label className="form-label">Society / Area</label>
              <select className="form-control" value={form.area}
                onChange={e => { setForm(f => ({ ...f, area: e.target.value })); setPreview(null); }}>
                <option value="">Select society...</option>
                {(initialArea && !areas.includes(initialArea) ? [initialArea, ...areas] : areas).map(a => <option key={a} value={a}>{a}</option>)}
              </select>
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <div className="form-group" style={{ flex: 1 }}>
                <label className="form-label">From</label>
                <input type="date" className="form-control" value={form.startDate}
                  onChange={e => { setForm(f => ({ ...f, startDate: e.target.value })); setPreview(null); }} />
              </div>
              <div className="form-group" style={{ flex: 1 }}>
                <label className="form-label">To</label>
                <input type="date" className="form-control" value={form.endDate}
                  onChange={e => { setForm(f => ({ ...f, endDate: e.target.value })); setPreview(null); }} />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Collected By (actual agent)</label>
              <select className="form-control" value={form.collectedBy}
                onChange={e => { setForm(f => ({ ...f, collectedBy: e.target.value })); setPreview(null); }}>
                <option value="">Select agent...</option>
                {agents.map(a => <option key={a._id} value={a._id}>{a.name} ({a.role})</option>)}
              </select>
            </div>

            {preview && (
              <div style={{ marginTop: 10, border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
                <div style={{ padding: '8px 12px', background: 'var(--cb-f8fafc)', fontSize: '.82rem', fontWeight: 600 }}>
                  {preview.count} payment(s) found — {preview.rows.filter(r => !r.alreadyCorrect).length} will change to {preview.agent}
                </div>
                <div style={{ maxHeight: 220, overflowY: 'auto' }}>
                  {preview.rows.map(r => (
                    <div key={r._id} style={{
                      padding: '6px 12px', fontSize: '.78rem', display: 'flex', justifyContent: 'space-between',
                      borderTop: '1px solid var(--border)', opacity: r.alreadyCorrect ? 0.5 : 1,
                    }}>
                      <span>{r.customer} <span style={{ color: 'var(--text-muted)' }}>· {r.receiptNumber}</span></span>
                      <span style={{ color: 'var(--text-muted)' }}>
                        {r.alreadyCorrect ? 'already correct' : `${r.currentCollector} → ${preview.agent}`}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            {preview && preview.count > 0 && (
              <button type="button" className="btn btn-primary" disabled={applying} onClick={handleApply}>
                {applying ? 'Applying...' : `Apply to ${preview.rows.filter(r => !r.alreadyCorrect).length} Payment(s)`}
              </button>
            )}
            <button type="submit" className="btn btn-secondary" disabled={!canPreview || loading}>
              {loading ? 'Checking...' : 'Preview'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Society-grouped monthly collection sheet — CableTV-only, regular (auto-renew) customers
// currently carrying a balance. Old/Running come from that customer's own renewal payment
// for the selected month (blank if the monthly cron/renewal hasn't run yet); Total is the
// live balance. D.O.C repeats the society's own collection date on every row, matching the
// paper ledger layout this replaces — it defaults to that society's last-month auto-derived
// date but an admin can override it per society/month (e.g. to schedule this month's visit).
export default function CollectionSheetPage() {
  useDarkReady();
  const navigate = useNavigate();
  const { phone: companyPhone } = useCompanyIdentity('catv');
  const [month, setMonth] = useState(format(new Date(), 'yyyy-MM'));
  const [societies, setSocieties] = useState([]);
  const [projected, setProjected] = useState(false);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  // Society picker — options loaded once; empty selection means "all societies".
  const [societyOptions, setSocietyOptions] = useState([]);
  const [selectedSocieties, setSelectedSocieties] = useState([]);
  const [excludeOffice, setExcludeOffice] = useState(false);
  const [includeIndex, setIncludeIndex] = useState(false);
  // "Show societies with no dues": lists every society with its agent and last collection date, so the
  // agent has the back data before going there. Remembered per browser; off by default.
  const [includeAll, setIncludeAll] = useState(() => { try { return localStorage.getItem('collectionSheet.includeAll') === '1'; } catch { return false; } });
  const toggleIncludeAll = (on) => { setIncludeAll(on); try { localStorage.setItem('collectionSheet.includeAll', on ? '1' : '0'); } catch { /* private mode etc. */ } };
  const [sortBy, setSortBy] = useState('society');
  const [pickerOpen, setPickerOpen] = useState(false);
  const [societySearch, setSocietySearch] = useState('');
  const pickerRef = useRef(null);
  const visibleSocietyOptions = societyOptions.filter(name => name.toLowerCase().includes(societySearch.trim().toLowerCase()));

  // Agent filter — "" means all agents. Picking one shows only the societies that agent
  // last actually collected in, and switches sort to Last Collection Date so their
  // societies line up datewise (matches how this is used to plan an agent's next round).
  const [agentOptions, setAgentOptions] = useState([]);
  const [agentFilter, setAgentFilter] = useState('');
  const [reassigning, setReassigning] = useState(null); // society name the modal was opened from, or null
  const [agentChoices, setAgentChoices] = useState([]); // [{ _id, name }] — field agents a society's agent can be hand-picked from

  useEffect(() => {
    reportAPI.collectionSheetSocieties().then(({ data }) => setSocietyOptions(data.data || [])).catch(() => {});
    reportAPI.collectionSheetAgents().then(({ data }) => setAgentOptions(data.data || [])).catch(() => {});
    reportAPI.collectionSheetAgentOptions().then(({ data }) => setAgentChoices(data.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    const onClickOutside = (e) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target)) { setPickerOpen(false); setSocietySearch(''); }
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  const toggleSociety = (name) => {
    setSelectedSocieties(prev => prev.includes(name) ? prev.filter(s => s !== name) : [...prev, name]);
  };

  const load = useCallback(async (m, sel, excOffice) => {
    setLoading(true);
    try {
      const params = { month: m };
      if (sel && sel.length) params.societies = sel.join(',');
      if (excOffice) params.excludeOffice = 'true';
      if (includeAll) params.includeAll = 'true';
      const { data } = await reportAPI.collectionSheet(params);
      setSocieties(data.data.societies || []);
      setProjected(!!data.data.projected);
    } catch (err) {
      toast.error('Failed to load collection sheet');
    } finally {
      setLoading(false);
    }
  }, [includeAll]);

  useEffect(() => { load(month, selectedSocieties, excludeOffice); }, [month, selectedSocieties, excludeOffice, load]);

  // dateStr === '' clears the override, reverting that society back to its auto-derived date.
  const handleDateChange = async (area, dateStr) => {
    setSocieties(prev => prev.map(g => g.area !== area ? g : {
      ...g,
      pDate: dateStr ? dateStr : g.pDateAuto,
      pDateOverridden: !!dateStr,
      pDateIsOffice: false,
    }));
    try {
      await reportAPI.collectionSheetSetDate({ area, month, date: dateStr || null });
      toast.success(dateStr ? 'Collection date updated' : 'Reverted to last month\'s date');
    } catch (err) {
      toast.error('Failed to save collection date');
      load(month, selectedSocieties, excludeOffice);
    }
  };

  // Hand-picks the agent shown for a society ('' clears it, back to the collections / assigned agent).
  const handleAgentPick = async (area, agentId) => {
    try {
      await reportAPI.collectionSheetSetAgent({ area, agentId: agentId || null });
      toast.success(agentId ? 'Agent set' : 'Agent reset to automatic');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save agent');
    } finally {
      load(month, selectedSocieties, excludeOffice);
    }
  };

  // Marks the whole society as collected at the office instead of a field visit — every
  // customer row's D.O.C cell then shows the literal text "OFFICE" instead of a date, until
  // a real date is picked or the override is reset back to the auto-derived date.
  const handleOfficeApply = async (area) => {
    setSocieties(prev => prev.map(g => g.area !== area ? g : {
      ...g,
      pDate: null,
      pDateOverridden: true,
      pDateIsOffice: true,
    }));
    try {
      await reportAPI.collectionSheetSetDate({ area, month, isOffice: true });
      toast.success('Marked as OFFICE');
    } catch (err) {
      toast.error('Failed to save collection date');
      load(month, selectedSocieties, excludeOffice);
    }
  };

  // Permanent office = the society defaults to OFFICE every month with no per-month
  // action; a real date (or the one-off Office button) picked for a specific month still
  // wins for that month only, then reverts to OFFICE again automatically the month after.
  const handleTogglePermanentOffice = async (area, permanent) => {
    setSocieties(prev => prev.map(g => g.area !== area ? g : {
      ...g,
      pDateIsOfficePermanent: permanent,
      ...(g.pDateOverridden ? {} : { pDate: permanent ? null : g.pDateAuto, pDateIsOffice: permanent }),
    }));
    try {
      await reportAPI.collectionSheetSetOfficePermanent({ area, permanent });
      toast.success(permanent ? 'Marked as permanent OFFICE' : 'Removed permanent OFFICE');
    } catch (err) {
      toast.error('Failed to update permanent office setting');
      load(month, selectedSocieties, excludeOffice);
    }
  };

  const exportExcel = async () => {
    setExporting(true);
    try {
      const params = { month };
      if (selectedSocieties.length) params.societies = selectedSocieties.join(',');
      if (excludeOffice) params.excludeOffice = 'true';
      if (sortBy !== 'society') params.sortBy = sortBy;
      if (agentFilter) params.agent = agentFilter;
      if (includeIndex) params.includeIndex = 'true';
      if (includeAll) params.includeAll = 'true';
      const { data } = await reportAPI.collectionSheetExport(params);
      const url = URL.createObjectURL(new Blob([data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `collection-sheet-${month}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error('Export failed');
    } finally {
      setExporting(false);
    }
  };

  // Agent filter narrows which societies are shown at all; sort then only reorders
  // within that narrowed set (and, via the export's own agent/sortBy params, the .xlsx).
  const visibleSocieties = useMemo(
    () => (agentFilter ? societies.filter(g => g.collectionAgent === agentFilter) : societies),
    [societies, agentFilter]
  );

  const totalCustomers = visibleSocieties.reduce((s, g) => s + g.rows.length, 0);
  const grandTotal = visibleSocieties.reduce((s, g) => s + g.rows.reduce((rs, r) => rs + (r.total || 0), 0), 0);

  const sortedSocieties = useMemo(() => {
    if (sortBy === 'lastCollection') {
      return [...visibleSocieties].sort((a, b) => {
        if (!a.pDateAuto && !b.pDateAuto) return a.area.localeCompare(b.area);
        if (!a.pDateAuto) return 1;
        if (!b.pDateAuto) return -1;
        return new Date(a.pDateAuto) - new Date(b.pDateAuto);
      });
    }
    if (sortBy === 'agent') {
      return [...visibleSocieties].sort((a, b) => {
        if (!a.collectionAgent && !b.collectionAgent) return a.area.localeCompare(b.area);
        if (!a.collectionAgent) return 1;
        if (!b.collectionAgent) return -1;
        const cmp = a.collectionAgent.localeCompare(b.collectionAgent);
        return cmp !== 0 ? cmp : a.area.localeCompare(b.area);
      });
    }
    return visibleSocieties;
  }, [visibleSocieties, sortBy]);

  const estimatedStartPages = useMemo(() => estimateStartPages(sortedSocieties), [sortedSocieties]);

  // Selecting a specific agent implies "show his round datewise" — auto-switch sort to
  // Last Collection Date (the user can still change it afterward if they want).
  const handleAgentFilterChange = (name) => {
    setAgentFilter(name);
    if (name) setSortBy('lastCollection');
  };

  return (
    <div>
      <style>{`
        @media print {
          .cs-no-print { display: none !important; }
          .cs-print-only { display: inline !important; }
          .page-content { padding: 0 !important; }
          thead { display: table-header-group; }
          @page { size: portrait; margin: 8mm 10mm 10mm 10mm; }
          .cs-table-wrap { overflow: visible !important; }
          .cs-table-wrap table { table-layout: fixed; width: 100% !important; font-size: 6.5pt !important; }
          .cs-table-wrap td, .cs-table-wrap th {
            padding: 2px 4px !important; white-space: nowrap !important;
            overflow-wrap: normal !important; word-break: normal !important;
            overflow: hidden !important; text-overflow: ellipsis !important;
          }
          /* Account No (2nd) sits right against House No (3rd) once nowrap lets a long
             number spill past its column edge — force some breathing room between them. */
          .cs-table-wrap td:nth-child(2), .cs-table-wrap th:nth-child(2) { padding-right: 8px !important; }
          .cs-table-wrap td:nth-child(3), .cs-table-wrap th:nth-child(3) { padding-left: 8px !important; }
          /* Package (4th) and Reason (10th, last) columns are the only free text — they
             wrap; every numeric/code column stays on one line so figures never break
             mid-number, and fixed per-column widths keep the whole table within page width. */
          .cs-table-wrap td:nth-child(4), .cs-table-wrap th:nth-child(4),
          .cs-table-wrap td:nth-child(10), .cs-table-wrap th:nth-child(10) {
            white-space: normal !important; overflow-wrap: break-word !important; word-break: break-word !important;
          }
          /* Reason is blank most of the time now (only shown when a due actually carries
             over from last month), and column 5 is an unused spacer — freed width goes to
             D.O.C (a full "dd/mm/yyyy" is 10 characters, the longest fixed-format value in
             the table) and the other previously-tight columns instead of sitting unused. */
          .cs-table-wrap th:nth-child(1), .cs-table-wrap td:nth-child(1)  { width: 3%; }
          .cs-table-wrap th:nth-child(2), .cs-table-wrap td:nth-child(2)  { width: 12%; }
          .cs-table-wrap th:nth-child(3), .cs-table-wrap td:nth-child(3)  { width: 9%; }
          .cs-table-wrap th:nth-child(4), .cs-table-wrap td:nth-child(4)  { width: 17%; }
          .cs-table-wrap th:nth-child(5), .cs-table-wrap td:nth-child(5)  { width: 1%; }
          .cs-table-wrap th:nth-child(6), .cs-table-wrap td:nth-child(6)  { width: 7%; }
          .cs-table-wrap th:nth-child(7), .cs-table-wrap td:nth-child(7)  { width: 7%; }
          .cs-table-wrap th:nth-child(8), .cs-table-wrap td:nth-child(8)  { width: 8%; }
          .cs-table-wrap th:nth-child(9), .cs-table-wrap td:nth-child(9)  { width: 16%; }
          .cs-table-wrap th:nth-child(10), .cs-table-wrap td:nth-child(10) { width: 20%; }
          /* Society header row (area name + collection date/agent line) — the blanket
             2px/4px cell padding above squeezed this to the point of being unreadable,
             and forced nowrap could clip the agent name entirely instead of wrapping. */
          .cs-table-wrap td.cs-society-row {
            padding: 6px 10px !important; white-space: normal !important; line-height: 1.5 !important;
          }
          /* Index page — its own printed page ahead of the main sheet. Societies below it
             keep flowing continuously (no forced break per society, so pages aren't wasted);
             Page No on the index is therefore a manual fill-in column, like Date. */
          .cs-index-page { page-break-after: always; border: none !important; }
          /* Sized from the society count (utils/collectionIndexPrint.js) so the index stays on ONE page;
             with no variables set (short lists) these fall back to the on-screen values, unchanged. */
          /* One line per row, always: the one-page fit above counts rows as single-line, so a long society or agent name
             that wrapped made a row taller and pushed the index onto a second page. Fixed columns + ellipsis instead. */
          .cs-index-page table { table-layout: fixed !important; width: 100% !important; }
          .cs-index-page th, .cs-index-page td { white-space: nowrap !important; overflow: hidden !important; text-overflow: ellipsis !important; }
          .cs-index-page th:nth-child(1), .cs-index-page td:nth-child(1) { width: 8%; }
          .cs-index-page th:nth-child(2), .cs-index-page td:nth-child(2) { width: 40%; }
          .cs-index-page th:nth-child(3), .cs-index-page td:nth-child(3) { width: 20%; }
          .cs-index-page th:nth-child(4), .cs-index-page td:nth-child(4) { width: 12%; }
          .cs-index-page th:nth-child(5), .cs-index-page td:nth-child(5) { width: 10%; }
          .cs-index-page th:nth-child(6), .cs-index-page td:nth-child(6) { width: 10%; }
          .cs-index-page table { font-size: var(--cs-index-font, .83rem) !important; }
          .cs-index-page td { padding-top: var(--cs-index-pad, 6px) !important; padding-bottom: var(--cs-index-pad, 6px) !important; line-height: var(--cs-index-lh, 1.6) !important; }
          .cs-index-page th { padding-top: var(--cs-index-pad-th, 8px) !important; padding-bottom: var(--cs-index-pad-th, 8px) !important; line-height: var(--cs-index-lh, 1.6) !important; }
          /* The app's own top bar (page title, search, notification bell) has no place on
             a printed sheet — replaced by the letterhead line below instead. */
          .topbar { display: none !important; }
          .cs-letterhead {
            display: grid !important; grid-template-columns: 1fr auto 1fr; align-items: center;
            /* The heading and the index table under it both sit 9.6mm higher than the old 19mm-top layout (two 20%
               steps). collectionIndexPrint.js still budgets the old 36mm letterhead, so the index now has 9.6mm to spare. */
            margin-top: 10.4mm; margin-bottom: 10px; font-weight: 700; font-size: 24pt;
          }
          .cs-letterhead-left { text-align: left; }
          .cs-letterhead-center { text-align: center; }
          .cs-letterhead-right { text-align: right; font-size: 18pt; white-space: nowrap; }
        }
      `}</style>

      <div className="cs-letterhead" style={{ display: 'none' }}>
        <span className="cs-letterhead-left">{format(new Date(`${month}-01`), 'MMM-yy').toUpperCase()}</span>
        <span className="cs-letterhead-center">॥ श्री गणेशाय नमः ॥</span>
        <span className="cs-letterhead-right">{companyPhone}</span>
      </div>

      <div className="cs-no-print flex items-center gap-3 mb-4" style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 12, rowGap: 14 }}>
        <button className="btn btn-secondary btn-sm" onClick={() => navigate('/reports')}>
          <ArrowLeft size={14} />
        </button>
        <div style={{ flex: '1 1 260px', minWidth: 260 }}>
          <h2 style={{ fontSize: '1.1rem', fontWeight: 700, margin: 0 }}>
            <ClipboardList size={16} style={{ verticalAlign: 'middle', marginRight: 6 }} />
            Monthly Collection Sheet
          </h2>
          <p className="text-muted text-small" style={{ margin: 0 }}>
            {projected
              ? "Projected for a future month — every active, regular-billing CableTV customer's current balance plus next renewal charge."
              : 'Society-wise dues for active, regular-billing CableTV customers with an outstanding balance.'}
          </p>
        </div>
        <input
          type="month"
          className="form-control"
          value={month}
          onChange={e => setMonth(e.target.value)}
          style={{ maxWidth: 160 }}
        />
        <button
          className="btn btn-secondary btn-sm"
          onClick={() => setMonth(format(addMonths(new Date(`${month}-01`), 1), 'yyyy-MM'))}
        >
          Next Month
        </button>
        <div ref={pickerRef} style={{ position: 'relative' }}>
          <button className="btn btn-secondary btn-sm" onClick={() => setPickerOpen(o => !o)}>
            {selectedSocieties.length ? `${selectedSocieties.length} Societies` : 'All Societies'} <ChevronDown size={13} />
          </button>
          {pickerOpen && (
            <div style={{
              position: 'absolute', top: '110%', right: 0, zIndex: 20, minWidth: 240,
              background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 10, boxShadow: '0 8px 24px rgba(0,0,0,.15)', padding: 8,
            }}>
              <input
                type="text"
                placeholder="Search societies…"
                className="input"
                value={societySearch}
                onChange={e => setSocietySearch(e.target.value)}
                style={{ width: '100%', marginBottom: 6, fontSize: '.85rem' }}
                autoFocus
              />
              <div style={{ display: 'flex', gap: 8, marginBottom: 6, padding: '0 4px' }}>
                <button className="btn btn-secondary btn-sm" style={{ flex: 1, fontSize: '.75rem' }} onClick={() => setSelectedSocieties([])}>All</button>
                <button className="btn btn-secondary btn-sm" style={{ flex: 1, fontSize: '.75rem' }} onClick={() => setSelectedSocieties(visibleSocietyOptions)}>
                  Select {societySearch.trim() ? 'Shown' : 'All'}
                </button>
              </div>
              <div style={{ maxHeight: 260, overflowY: 'auto' }}>
                {visibleSocietyOptions.length === 0 ? (
                  <div style={{ padding: '10px 8px', fontSize: '.8rem', color: 'var(--text-muted)' }}>No societies match "{societySearch}"</div>
                ) : visibleSocietyOptions.map(name => (
                  <label key={name} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 8px', fontSize: '.85rem', cursor: 'pointer' }}>
                    <input type="checkbox" checked={selectedSocieties.includes(name)} onChange={() => toggleSociety(name)} />
                    {name}
                  </label>
                ))}
              </div>
            </div>
          )}
        </div>
        <label className="cs-no-print" style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '.85rem', cursor: 'pointer' }}>
          <input type="checkbox" checked={excludeOffice} onChange={e => setExcludeOffice(e.target.checked)} />
          Exclude Office
        </label>
        <label className="cs-no-print" style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '.85rem', cursor: 'pointer' }}>
          <input type="checkbox" checked={includeIndex} onChange={e => setIncludeIndex(e.target.checked)} />
          Include Index Page
        </label>
        <label className="cs-no-print" style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '.85rem', cursor: 'pointer' }}>
          <input type="checkbox" checked={includeAll} onChange={e => toggleIncludeAll(e.target.checked)} />
          Show societies with no dues
        </label>
        <select
          className="cs-no-print form-control"
          value={agentFilter}
          onChange={e => handleAgentFilterChange(e.target.value)}
          style={{ padding: '4px 10px', fontSize: '.8rem', minWidth: 140, flexShrink: 0 }}
        >
          <option value="">All Agents</option>
          {agentOptions.map(name => <option key={name} value={name}>{name}</option>)}
        </select>
        <select
          className="cs-no-print form-control"
          value={sortBy}
          onChange={e => setSortBy(e.target.value)}
          style={{ padding: '4px 10px', fontSize: '.8rem', minWidth: 190, flexShrink: 0 }}
        >
          <option value="society">Sort: Society (A-Z)</option>
          <option value="lastCollection">Sort: Last Collection Date</option>
          <option value="agent">Sort: Collection Agent</option>
        </select>
        <button className="btn btn-secondary btn-sm" onClick={exportExcel} disabled={loading || exporting || !visibleSocieties.length}>
          {exporting ? <Loader size={14} style={{ animation: 'spin 1s linear infinite' }} /> : <Download size={14} />}
          {exporting ? 'Preparing…' : 'Export to Excel'}
        </button>
        <button className="btn btn-primary btn-sm" onClick={() => window.print()} disabled={loading || !visibleSocieties.length}>
          <Printer size={14} /> Print
        </button>
      </div>

      {projected && (
        <div className="cs-no-print card mb-4" style={{ padding: '10px 16px', background: 'var(--cb-fffbeb)', borderColor: 'var(--cb-fde68a)', fontSize: '.85rem', color: 'var(--c-92400e)' }}>
          <strong>Projected sheet</strong> — {month} hasn't been billed yet. Old/Running/Total below are a forecast (current balance + next renewal charge), not actual generated bills.
        </div>
      )}

      <div className="cs-no-print card mb-4" style={{ padding: '10px 16px', display: 'flex', gap: 24, fontSize: '.85rem' }}>
        <span><strong>{visibleSocieties.length}</strong> societies</span>
        <span><strong>{totalCustomers}</strong> customers {projected ? 'projected' : 'due'}</span>
        <span>Total {projected ? 'projected' : 'outstanding'}: <strong>{fmt(grandTotal)}</strong></span>
      </div>

      {includeIndex && !loading && sortedSocieties.length > 0 && (
        <div className="card cs-index-page" style={{ padding: 0, overflow: 'hidden', marginBottom: 16, ...indexPrintVars(sortedSocieties.length) }}>
          <div className="cs-no-print" style={{ padding: '10px 16px', fontSize: '.78rem', color: 'var(--text-muted)', background: 'var(--bg-page, #f3f4f6)', borderBottom: '1px solid var(--border)' }}>
            Page No is an estimate; it can drift by a page or two depending on how it actually prints.
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '.83rem' }}>
            <thead>
              <tr style={{ background: 'var(--bg)', textAlign: 'left' }}>
                {['Sr No', 'Society', 'Agent', 'P.Date', 'Date', 'Page No'].map((h, idx) => (
                  <th key={idx} style={{ padding: '8px 12px', borderBottom: '1px solid var(--border)' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sortedSocieties.map((g, i) => (
                <tr key={g.area} style={{ borderBottom: '1px solid var(--border)' }}>
                  <td style={{ padding: '6px 12px', color: 'var(--text-muted)' }}>{i + 1}</td>
                  <td style={{ padding: '6px 12px' }}>
                    <a className="cs-no-print" href={`#${societyAnchorId(g.area)}`} style={{ color: 'inherit', textDecoration: 'underline' }}>{g.area}</a>
                    <span className="cs-print-only" style={{ display: 'none' }}>{g.area}</span>
                  </td>
                  <td style={{ padding: '6px 12px', whiteSpace: 'nowrap' }}>{g.collectionAgent || ''}</td>
                  <td style={{ padding: '6px 12px', whiteSpace: 'nowrap' }}>{g.pDateAuto ? fmtDate(g.pDateAuto) : (g.pDateIsOffice ? 'OFFICE' : fmtDate(g.pDate))}</td>
                  <td style={{ padding: '6px 12px' }}></td>
                  <td style={{ padding: '6px 12px' }}>{estimatedStartPages[i]}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {loading ? (
        <div style={{ textAlign: 'center', padding: 60 }}><Loader size={22} style={{ animation: 'spin 1s linear infinite' }} /></div>
      ) : visibleSocieties.length === 0 ? (
        <div className="card empty-state" style={{ padding: 48 }}>
          <ClipboardList size={32} />
          <h3>Nothing due</h3>
          <p>{agentFilter
            ? `No societies collected by ${agentFilter} last month.`
            : projected
              ? 'No active, regular-billing CableTV customers found for this month.'
              : 'No active, regular-billing CableTV customers currently carry an outstanding balance.'}</p>
        </div>
      ) : (
        <div className="card cs-table-wrap" style={{ padding: 0, overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '.83rem' }}>
            <thead>
              <tr style={{ background: 'var(--bg)', textAlign: 'left' }}>
                <th style={{ padding: '8px 12px', borderBottom: '1px solid var(--border)', width: 28 }} />
                {['Account No', 'House No', 'Package', '', 'Old', 'Running', 'Total', 'D.O.C', 'Reason'].map((h, idx) => (
                  <th key={idx} style={{ padding: '8px 12px', borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            {sortedSocieties.map(g => (
              <tbody key={g.area} id={societyAnchorId(g.area)}>
                <tr>
                  <td colSpan={10} className="cs-society-row" style={{
                    padding: '7px 12px', fontWeight: 700, background: 'var(--bg-page, #f3f4f6)',
                    borderTop: '1px solid var(--border)', borderBottom: '1px solid var(--border)',
                  }}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      {g.area}
                      <button
                        className="cs-no-print btn btn-secondary btn-sm"
                        style={{ padding: '2px 8px', fontSize: '.75rem', fontWeight: 500 }}
                        onClick={() => setReassigning(g.area)}
                      >
                        <Users size={12} /> Reassign Collector
                      </button>
                      <span className="cs-no-print" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontWeight: 500, fontSize: '.8rem', color: 'var(--text-muted)' }}>
                        — {g.pDateOverridden ? 'Collection date' : 'Last collection'}: {g.pDateIsOffice ? (g.pDateIsOfficePermanent && !g.pDateOverridden ? 'OFFICE (Permanent)' : 'OFFICE') : ''}
                        <input
                          type="date"
                          className="form-control"
                          value={toInputDate(g.pDate)}
                          onChange={e => handleDateChange(g.area, e.target.value)}
                          style={{ padding: '2px 6px', fontSize: '.8rem', width: 140 }}
                        />
                        {g.pDateIsOfficePermanent ? (
                          <button
                            className="btn btn-secondary btn-sm"
                            style={{ padding: '2px 8px', fontSize: '.75rem' }}
                            onClick={() => handleTogglePermanentOffice(g.area, false)}
                          >
                            Remove Permanent
                          </button>
                        ) : (
                          <>
                            <button
                              className="btn btn-secondary btn-sm"
                              style={{ padding: '2px 8px', fontSize: '.75rem' }}
                              onClick={() => handleOfficeApply(g.area)}
                            >
                              Office
                            </button>
                            <button
                              className="btn btn-secondary btn-sm"
                              style={{ padding: '2px 8px', fontSize: '.75rem' }}
                              onClick={() => handleTogglePermanentOffice(g.area, true)}
                            >
                              Make Permanent
                            </button>
                          </>
                        )}
                        {g.pDateOverridden && (
                          <button
                            className="btn btn-secondary btn-sm"
                            style={{ padding: '2px 8px', fontSize: '.75rem' }}
                            onClick={() => handleDateChange(g.area, '')}
                          >
                            Reset
                          </button>
                        )}
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                          — Agent:
                          <select
                            className="form-control"
                            value={g.collectionAgentSource === 'manual' ? String(g.collectionAgentId) : ''}
                            onChange={e => handleAgentPick(g.area, e.target.value)}
                            style={{ padding: '2px 6px', fontSize: '.8rem', width: 150 }}
                            title="Pick the agent for this society, or leave on Auto"
                          >
                            <option value="">{g.collectionAgent && g.collectionAgentSource !== 'manual' ? `Auto — ${g.collectionAgent}` : 'Auto — none'}</option>
                            {agentChoices.map(a => <option key={a._id} value={a._id}>{a.name}</option>)}
                          </select>
                          {g.collectionAgentSource === 'assigned' && <span title="No field agent has collected here yet; this is the agent assigned to the society">(assigned)</span>}
                        </span>
                      </span>
                      <span className="cs-print-only" style={{ display: 'none', fontWeight: 500, fontSize: '.8rem', color: 'var(--text-muted)' }}>
                        — {g.pDateOverridden ? 'Collection date' : 'Last collection'}: {g.pDateIsOffice ? 'OFFICE' : fmtDate(g.pDate)}
                        {g.collectionAgent && ` — Agent: ${g.collectionAgent}`}
                      </span>
                    </span>
                  </td>
                </tr>
                {g.rows.map((r, i) => (
                  <tr key={r._id} style={{ borderBottom: '1px solid var(--border)' }}>
                    <td style={{ padding: '6px 12px', color: 'var(--text-muted)' }}>{i + 1}</td>
                    <td style={{ padding: '6px 12px', fontFamily: 'monospace' }}>{r.accountNumber || '—'}</td>
                    <td style={{ padding: '6px 12px' }}>{r.houseNumber || '—'}</td>
                    <td style={{ padding: '6px 12px' }}>{r.package || '—'}</td>
                    <td style={{ padding: '6px 12px' }} />
                    <td style={{ padding: '6px 12px' }}>{fmt(r.old)}</td>
                    <td style={{ padding: '6px 12px' }}>{fmt(r.running)}</td>
                    <td style={{ padding: '6px 12px', fontWeight: 700 }}>{fmt(r.total)}</td>
                    <td style={{ padding: '6px 12px', whiteSpace: 'nowrap' }}>{g.pDateIsOffice ? 'OFFICE' : fmtDate(g.pDate)}</td>
                    <td style={{ padding: '6px 12px', color: 'var(--c-dc2626)' }}>{r.reason || ''}</td>
                  </tr>
                ))}
              </tbody>
            ))}
          </table>
        </div>
      )}

      {reassigning && (
        <ReassignCollectorModal
          initialArea={reassigning}
          onClose={() => setReassigning(null)}
          onApplied={() => { setReassigning(null); load(month, selectedSocieties, excludeOffice); }}
        />
      )}
    </div>
  );
}
